import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { useGameStore } from './store/gameStore';
import { Home } from './pages/Home';
import { BottomNav } from './components/BottomNav';
import { Lobby } from './pages/Lobby';
import { Game } from './pages/Game';
import { Results } from './pages/Results';
import { ConfigScreen } from './pages/ConfigScreen';
import { Versus } from './pages/Versus';
import GemLab from './pages/GemLab';
import { PmQuestion } from './components/PmQuestion';
import { Profile } from './pages/Profile';
import { Leaderboard } from './pages/Leaderboard';
import { AdminLayout, Dashboard, QuestionFactory, QuestionDatabase, ReviewQueue, CoverageMap, MatchAnalytics, Settings } from './pages/admin';
import { isNative } from './utils/platform';
import { PsycheMatrixSidebar } from './components/PsycheMatrixSidebar';
import { viewToPath, pathToView } from './utils/navigation';
import { inviteCodeFromUrl } from './lib/invite';
import { trackAppOpens } from './lib/appEvents';
import { listenForReminderTaps, refreshReminders } from './lib/dailyReminder';
import { runDevDemo } from './lib/devDemo';

/** Syncs gameStore.view ↔ React Router URL */
function NavigationSync() {
  const navigate = useNavigate();
  const location = useLocation();
  const view = useGameStore((s) => s.view);
  const lastSyncedView = useRef(view);
  const lastSyncedPath = useRef(location.pathname);

  // Store view changed → update URL
  useEffect(() => {
    if (view === lastSyncedView.current) return;
    lastSyncedView.current = view;
    // A screen change is a fresh page to the player: scroll left over from
    // the previous screen (a browsed leaderboard, a long profile) must not
    // carry into the next one and clip its top.
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const targetPath = viewToPath(view);
    if (location.pathname !== targetPath && !location.pathname.startsWith('/admin')) {
      lastSyncedPath.current = targetPath;
      navigate(targetPath, { replace: true });
    }
  }, [view, navigate, location.pathname]);

  // URL changed (browser back/forward) → update store
  useEffect(() => {
    if (location.pathname === lastSyncedPath.current) return;
    lastSyncedPath.current = location.pathname;
    const targetView = pathToView(location.pathname);
    if (targetView && targetView !== view) {
      lastSyncedView.current = targetView;
      useGameStore.getState().setView(targetView);
    }
  }, [location.pathname, view]);

  return null;
}

/** The store writes `error` in six places (solo start, daily, quick match,
 *  rooms) and until now nothing read it: a failed start bounced the player
 *  to Home in silence. One strip, plain words, gone after a few seconds or
 *  a tap. Raw edge messages stay in the console. */
function ErrorBanner() {
  const error = useGameStore((s) => s.error);
  const setError = useGameStore((s) => s.setError);
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(t);
  }, [error, setError]);
  if (!error) return null;
  const offline = /fetch|network|Failed to|load failed|timeout/i.test(error);
  const roomSentence = /room|code/i.test(error);
  const text = offline
    ? 'No connection. Check your network and try again.'
    : roomSentence
      ? error.replace(/^Edge Function error \(\d+\): /, '').replace(/^\{.*"error":"([^"]+)".*\}$/, '$1')
      : 'Something went wrong starting the game. Please try again.';
  return (
    <div className="app-error" role="alert" onClick={() => setError(null)}>
      {text}
    </div>
  );
}

/** Android hardware back. The router navigates with replace, so the
 *  WebView has no history and Capacitor's default for a back press with no
 *  listener is to close the app. Every screen maps to its parent instead; a
 *  match in progress routes the press to the Game screen's leave flow,
 *  Home really exits. */
function useAndroidBack() {
  useEffect(() => {
    if (!isNative) return;
    const handle = CapApp.addListener('backButton', () => {
      const s = useGameStore.getState();
      switch (s.view) {
        case 'home':
          CapApp.exitApp();
          break;
        case 'game':
          // The Game screen decides: solo leaves, a 1v1 asks first.
          window.dispatchEvent(new Event('wot-back'));
          break;
        case 'lobby':
        case 'versus':
        case 'results':
          s.resetGame();
          s.setView('home');
          break;
        default:
          s.setView('home');
      }
    });
    return () => { handle.then((h) => h.remove()); };
  }, []);
}

/** Invite links: https://<web>/j/CODE. On the web the code is in the first
 *  URL; on Android an App Link arrives through appUrlOpen while the app is
 *  running or being launched. Either way the player lands in the lobby.
 *  The code is read at module load: the router rewrites the URL to '/' in
 *  a child effect that runs before any effect of App itself. */
const INITIAL_INVITE_CODE = typeof window !== 'undefined' ? inviteCodeFromUrl(window.location.href) : null;
// SEO pages link into a realm: /play?realm=CHRONICLES opens Solo Setup with
// that realm selected. Read at module load for the same reason as above.
// A shared Daily card links here: play the same ten at once. If today's
// set was already played on this device, Home shows the result instead.
const INITIAL_DAILY = (() => {
  try { return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('daily') === '1'; }
  catch { return false; }
})();

/** Today's Daily, unless this device already played it: then Home, which
 *  shows the result. Shared by the ?daily=1 link and the reminder tap. */
function openTodaysDaily() {
  let playedToday = false;
  try {
    const saved = JSON.parse(localStorage.getItem('wot_daily_v1') || 'null');
    playedToday = saved?.key === new Date().toISOString().slice(0, 10);
  } catch { /* storage blocked: just play */ }
  const st = useGameStore.getState();
  if (playedToday) st.setView('home');
  else void st.startDailyMatch();
}

const INITIAL_PLAY_REALM = (() => {
  try {
    if (typeof window === 'undefined' || window.location.pathname !== '/play') return null;
    const r = new URLSearchParams(window.location.search).get('realm');
    return r ? r.toUpperCase() : null;
  } catch { return null; }
})();

function useInviteLinks() {
  useEffect(() => {
    // The join waits for the first render to settle: firing it inside the
    // mount effect interleaved with the router's view/URL sync and the join's
    // own view changes, and the error from a dead room was lost in that
    // shuffle before the banner could show it.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const code = INITIAL_INVITE_CODE;
    if (code) {
      timer = setTimeout(() => { void useGameStore.getState().joinRoom(code, 'link'); }, 600);
    } else if (INITIAL_PLAY_REALM) {
      const st = useGameStore.getState();
      st.setGameMode('solo');
      st.setSelectedRealm(INITIAL_PLAY_REALM);
      st.setView('config');
    } else if (INITIAL_DAILY) {
      timer = setTimeout(openTodaysDaily, 600);
    }
    if (!isNative) return () => { if (timer) clearTimeout(timer); };
    const handle = CapApp.addListener('appUrlOpen', ({ url }) => {
      const c = inviteCodeFromUrl(url);
      if (c) setTimeout(() => { void useGameStore.getState().joinRoom(c, 'link'); }, 300);
    });
    return () => { if (timer) clearTimeout(timer); handle.then((h) => h.remove()); };
  }, []);
}

/** The visit log (lib/appEvents.ts) and the Daily reminder (lib/dailyReminder.ts):
 *  an open is logged, the next reminders are rebuilt on every open and
 *  return, and a tap on one goes into the Daily from any resting screen,
 *  never out of a game in progress. */
function useAppOpensAndReminders() {
  useEffect(() => {
    const stopOpens = trackAppOpens();
    void refreshReminders();
    const stopTaps = listenForReminderTaps(() => {
      const view = useGameStore.getState().view;
      if (view === 'game' || view === 'lobby' || view === 'versus') return;
      setTimeout(openTodaysDaily, 600);
    });
    const resume = isNative ? CapApp.addListener('resume', () => { void refreshReminders(); }) : null;
    return () => {
      stopOpens();
      stopTaps();
      void resume?.then((h) => h.remove());
    };
  }, []);
}

/** Game pages layout wrapper (with sidebars + bottom nav) */
function GameLayout({ children }: { children: React.ReactNode }) {
  const correctAnswers = useGameStore((s) => s.correctAnswers);
  const totalRounds = useGameStore((s) => s.totalRounds);
  const totalResponseTimeMs = useGameStore((s) => s.totalResponseTimeMs);
  const pmEnabled = useGameStore((s) => s.pmPromotionsEnabled);

  const gameState = {
    accuracy: totalRounds > 0 ? correctAnswers / totalRounds : undefined,
    avgSpeed: totalRounds > 0 ? totalResponseTimeMs / totalRounds / 1000 : undefined,
  };

  return (
    <div className="app-layout">
      {pmEnabled && <PsycheMatrixSidebar position="left" />}
      <main className="app-main">
        {children}
      </main>
      {pmEnabled && <PsycheMatrixSidebar position="right" gameState={gameState} />}
    </div>
  );
}

export function App() {
  const view = useGameStore((s) => s.view);
  const initListeners = useGameStore((s) => s.initListeners);
  const cleanupListeners = useGameStore((s) => s.cleanupListeners);
  const fetchSettings = useGameStore((s) => s.fetchSettings);
  const fetchRealmCounts = useGameStore((s) => s.fetchRealmCounts);
  const location = useLocation();
  useAndroidBack();
  useInviteLinks();
  useAppOpensAndReminders();

  useEffect(() => {
    initListeners();
    fetchSettings();
    fetchRealmCounts();
    // ?demo=results|review (dev only): a finished screen without a game; see lib/devDemo.ts.
    const demo = import.meta.env.DEV ? setTimeout(runDevDemo, 400) : null;
    return () => { if (demo) clearTimeout(demo); cleanupListeners(); };
  }, []);

  // On native the admin route is not mounted at all, so the path must
  // fall through to the game instead of rendering a blank screen.
  const isAdmin = !isNative && location.pathname.startsWith('/sys/wotgate');

  // Game content rendered based on store view (existing pattern preserved)
  const gameContent = (
    <>
      <ErrorBanner />
      {view === 'home' && <Home />}
      {view === 'lobby' && <Lobby />}
      {view === 'versus' && <Versus />}
      {view === 'game' && <Game />}
      {view === 'results' && <Results />}
      {view === 'config' && <ConfigScreen />}
      {view === 'profile' && <Profile />}
      {view === 'leaderboard' && <Leaderboard />}
      {/* The tab bar lives on every top-level screen, not just Home:
          stranding Leaderboard behind a lone back arrow was the first
          piece of tester UX feedback. */}
      {['home', 'config', 'profile', 'leaderboard'].includes(view) && <BottomNav />}
    </>
  );

  return (
    <>
      <NavigationSync />
      <Routes>
        {/* Design lab. Dev builds only, stripped from production bundles. */}
        {import.meta.env.DEV && <Route path="/sys/gemlab" element={<GemLab />} />}
        {/* dev only: the eleventh question on its own, no match needed */}
        {import.meta.env.DEV && <Route path="/sys/pmq" element={<PmQuestion onDone={() => { window.location.href = '/'; }} />} />}

        {/* Admin routes — own layout, no sidebars */}
        {/* The admin panel does not exist in the store build: the route is
            only mounted on the web. Server-side gates (ADMIN_EMAILS on the
            edge functions, is_admin() in RLS) remain the real security. */}
        {!isNative && (
        <Route path="/sys/wotgate" element={<AdminLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="factory" element={<QuestionFactory />} />
          <Route path="database" element={<QuestionDatabase />} />
          <Route path="review" element={<ReviewQueue />} />
          <Route path="coverage" element={<CoverageMap />} />
          <Route path="analytics" element={<MatchAnalytics />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        )}

        {/* All game routes — wrapped in GameLayout with sidebars */}
        <Route path="*" element={
          isAdmin ? null : <GameLayout>{gameContent}</GameLayout>
        } />
      </Routes>
    </>
  );
}
